// Import Loader
import '@react-sigma/core/lib/style.css';

// Added for warning icon
// Import Sigma type and SigmaContainer
import { useSigma } from '@react-sigma/core';
// Import Node programs
import Graph from 'graphology';
import { FC, memo, useCallback, useEffect, useRef } from 'react';

// Add Asset interface near the top of the file with other interfaces
import { CLUSTER_CONFIG } from '@/components/nodeGraph/config/graphConfig';
import {
  addPaddingPoints,
  calculateConvexHull,
  smoothPoints,
} from '@/components/nodeGraph/utils/layout';
import { Cluster } from '@/components/nodeGraph/utils/types';

/**
 * ClusterCanvas - Visual Cluster Overlay Component
 *
 * This component renders colored hulls (boundaries) around groups of related assets
 * in the NodeGraph visualization. It creates visual cluster boundaries using HTML5
 * Canvas with sophisticated geometric calculations and smooth rendering.
 *
 * Features:
 * - Convex Hull Calculation: Creates boundaries around grouped assets
 * - Smooth Curves: Uses quadratic Bézier curves for aesthetic appeal
 * - Dynamic Sizing: Adapts to graph container and pixel ratio
 * - Theme Support: Adapts colors and styling to light/dark themes
 * - Selection Highlighting: Purple highlighting for selected clusters
 * - Cluster Labels: Shows cluster name and node count at centroid
 * - Camera Synchronization: Clusters move with graph pan/zoom
 *
 * Technical Implementation:
 * - Canvas-based rendering for performance
 * - Viewport coordinate conversion for accuracy
 * - Padding points for better boundary shapes
 * - Smoothing algorithms for curved edges
 * - Shadow effects for depth perception
 * - Semi-transparent fills (18% opacity)
 *
 * Performance Optimizations:
 * - Conditional rendering (hides when nodes selected)
 * - Debounced redraws to prevent excessive updates
 * - Camera event synchronization
 * - Retina display support with pixel ratio scaling
 *
 * Use Cases:
 * - Geographic clustering (Country-based groups)
 * - Network clustering (AS-based groups)
 * - Status clustering (Active/Inactive groups)
 * - Operational insights and pattern recognition
 */
/**
 * ClusterCanvas component - Memoized to prevent unnecessary re-renders
 * Only re-renders when clusters, graph, hasSelection, currentTheme, or selectedClusterValue change
 */
const ClusterCanvasComponent: FC<{
  clusters: Cluster[];
  graph: Graph | null;
  hasSelection: boolean;
  currentTheme: 'light' | 'dark';
  selectedClusterValue?: string | null;
}> = ({
  clusters,
  graph,
  hasSelection,
  currentTheme,
  selectedClusterValue,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sigma = useSigma();
  const isFirstRender = useRef(true);
  const redrawTimeoutRef = useRef<number | null>(null);
  const hullDataRef = useRef<
    { value: string; hullPoints: { x: number; y: number }[] }[]
  >([]);

  // Function to redraw the canvas
  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !graph || !sigma) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Match canvas size to sigma container
    const container = sigma.getContainer();
    const pixelRatio = window.devicePixelRatio || 1;

    // Set canvas size accounting for pixel ratio
    canvas.width = container.offsetWidth * pixelRatio;
    canvas.height = container.offsetHeight * pixelRatio;
    canvas.style.width = `${container.offsetWidth}px`;
    canvas.style.height = `${container.offsetHeight}px`;

    // Scale context for retina displays
    ctx.scale(pixelRatio, pixelRatio);

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // ✅ CRITICAL: Clear hull data ref before redrawing to prevent accumulation
    hullDataRef.current = [];

    // Only draw clusters if there's no selection
    if (!hasSelection) {
      clusters.forEach(cluster => {
        // Get viewport points for existing nodes in this cluster
        const existingNodeIds = cluster.nodes.filter(nodeId =>
          graph.hasNode(nodeId)
        );
        const viewportPoints = existingNodeIds
          .map(nodeId => {
            const x = graph.getNodeAttribute(nodeId, 'x');
            const y = graph.getNodeAttribute(nodeId, 'y');
            // Convert directly to viewport coordinates
            return sigma.graphToViewport({ x, y });
          })
          // Filter out any points that might be NaN after conversion (optional but safe)
          .filter(p => p && !isNaN(p.x) && !isNaN(p.y));

        // Need at least one valid point to draw a hull or label
        if (viewportPoints.length < 1) return;

        // Perform hull calculations on viewport points
        const paddedPoints = addPaddingPoints(
          viewportPoints,
          CLUSTER_CONFIG.hullPadding
        );
        const hull = calculateConvexHull(paddedPoints);
        const smoothedHull = smoothPoints(
          hull,
          CLUSTER_CONFIG.hullSmoothTension
        );

        // Store hull points (which are already viewport points) for click detection
        hullDataRef.current.push({
          value: cluster.value,
          hullPoints: smoothedHull,
        });

        // Draw hull with quadratic curves (using smoothedHull which is in viewport coords)
        ctx.beginPath();
        if (smoothedHull.length > 0) {
          ctx.moveTo(smoothedHull[0].x, smoothedHull[0].y);
        }
        for (let i = 0; i < smoothedHull.length; i += 2) {
          const p2 = smoothedHull[(i + 1) % smoothedHull.length];
          const p3 = smoothedHull[(i + 2) % smoothedHull.length];
          ctx.quadraticCurveTo(p2.x, p2.y, p3.x, p3.y);
        }
        ctx.closePath();

        // Add shadow for depth
        ctx.shadowColor = cluster.color;
        ctx.shadowBlur = CLUSTER_CONFIG.shadowBlur;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;

        // Fill with cluster color or purple if selected
        const isSelected = cluster.value === selectedClusterValue;
        const hullColor = isSelected
          ? CLUSTER_CONFIG.selectedColor
          : cluster.color;
        ctx.fillStyle = hullColor;
        ctx.globalAlpha = CLUSTER_CONFIG.fillOpacity;
        ctx.fill();
        ctx.globalAlpha = 1.0;

        // Reset shadow before stroke
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;

        // Add border with gradient
        const gradient = ctx.createLinearGradient(
          0,
          0,
          canvas.width,
          canvas.height
        );
        gradient.addColorStop(0, cluster.color.replace('0.2', '0.4'));
        gradient.addColorStop(1, cluster.color.replace('0.2', '0.6'));

        ctx.strokeStyle = gradient;
        ctx.lineWidth = isSelected
          ? CLUSTER_CONFIG.borderWidth.selected
          : CLUSTER_CONFIG.borderWidth.normal;
        ctx.stroke();

        // Calculate centroid using viewport points
        const centroid = {
          x:
            viewportPoints.reduce(
              (sum: number, p: { x: number }) => sum + p.x,
              0
            ) / viewportPoints.length,
          y:
            viewportPoints.reduce(
              (sum: number, p: { y: number }) => sum + p.y,
              0
            ) / viewportPoints.length,
        };

        // Calculate node type breakdown for better labeling
        const nodeTypeCounts = cluster.nodes.reduce(
          (counts, nodeId) => {
            if (graph.hasNode(nodeId)) {
              const nodeType =
                graph.getNodeAttribute(nodeId, 'nodeType') || 'asset';
              counts[nodeType] = (counts[nodeType] || 0) + 1;
            }
            return counts;
          },
          {} as Record<string, number>
        );

        // Create label with true count from backend
        const assetCount = nodeTypeCounts.asset || 0;
        const seedCount = nodeTypeCounts.seed || 0;
        // Use true count from backend if available, otherwise fall back to visible nodes
        const totalCount = cluster.count ?? cluster.nodes.length;
        let label = `${cluster.value} (${totalCount})`;

        // Add breakdown if there are both types (using visible counts for breakdown)
        if (assetCount > 0 && seedCount > 0) {
          label = `${cluster.value} (${totalCount} total)`;
        } else if (seedCount > 0 && assetCount === 0) {
          label = `${cluster.value} (${totalCount} seeds)`;
        }
        ctx.font = ` ${CLUSTER_CONFIG.label.fontSize}px sans-serif`;
        const metrics = ctx.measureText(label);
        const padding = CLUSTER_CONFIG.label.padding;
        const bgWidth = metrics.width + padding * 2;
        const bgHeight = 24; // Increased height for better readability
        ctx.fillStyle =
          currentTheme === 'light'
            ? `rgba(255, 255, 255, ${CLUSTER_CONFIG.label.backgroundOpacity})`
            : `rgba(17, 24, 39, ${CLUSTER_CONFIG.label.backgroundOpacity})`;
        ctx.beginPath();
        ctx.roundRect(
          centroid.x - bgWidth / 2,
          centroid.y - bgHeight / 2,
          bgWidth,
          bgHeight,
          CLUSTER_CONFIG.label.borderRadius
        );
        ctx.fill();

        // Add subtle border
        ctx.strokeStyle =
          currentTheme === 'light'
            ? 'rgba(209, 213, 219, 0.5)'
            : 'rgba(75, 85, 99, 0.5)';
        ctx.lineWidth = 1;
        ctx.stroke();

        // Draw label text
        ctx.fillStyle =
          currentTheme === 'light' ? 'rgb(17, 24, 39)' : 'rgb(243, 244, 246)';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(label, centroid.x, centroid.y);

        if (isSelected) {
          ctx.lineWidth = CLUSTER_CONFIG.borderWidth.selected;
          ctx.stroke();
        }

        // Draw hull path
        ctx.beginPath();
        if (smoothedHull.length > 0) {
          ctx.moveTo(smoothedHull[0].x, smoothedHull[0].y);
        }
        for (let i = 0; i < smoothedHull.length; i += 2) {
          const p2 = smoothedHull[(i + 1) % smoothedHull.length];
          const p3 = smoothedHull[(i + 2) % smoothedHull.length];
          ctx.quadraticCurveTo(p2.x, p2.y, p3.x, p3.y);
        }
        ctx.closePath();

        ctx.save();
        ctx.globalAlpha = CLUSTER_CONFIG.fillOpacity;
        ctx.fillStyle = hullColor;
        ctx.fill();
        ctx.restore();

        ctx.save();
        ctx.globalAlpha = 1.0;
        ctx.strokeStyle = hullColor;
        ctx.lineWidth = isSelected
          ? CLUSTER_CONFIG.borderWidth.selected
          : CLUSTER_CONFIG.borderWidth.normal;
        ctx.stroke();
        ctx.restore();
      });
    }
  }, [
    clusters,
    graph,
    sigma,
    hasSelection,
    currentTheme,
    selectedClusterValue,
  ]);

  // Handle camera changes
  useEffect(() => {
    if (!sigma) return;

    // Register camera update handler
    sigma.getCamera().on('updated', redraw);
    sigma.on('afterRender', redraw);

    // Force initial camera sync if this is the first render
    if (isFirstRender.current) {
      isFirstRender.current = false;
      // Trigger a camera update to force initial sync
      sigma
        .getCamera()
        .animate(
          { x: sigma.getCamera().x, y: sigma.getCamera().y },
          { duration: 0 }
        );
    }

    return () => {
      sigma.getCamera().removeListener('updated', redraw);
      sigma.removeListener('afterRender', redraw); // <-- Fix: remove afterRender listener
    };
  }, [sigma, redraw]);

  // Handle initial draw and cluster changes with delay
  useEffect(() => {
    // Clear any existing timeout
    if (redrawTimeoutRef.current) {
      window.clearTimeout(redrawTimeoutRef.current);
    }

    // Set a new timeout to redraw after nodes have settled
    redrawTimeoutRef.current = window.setTimeout(
      redraw,
      CLUSTER_CONFIG.redrawDebounce
    );

    // Cleanup function to clear timeout on unmount or dependency change
    return () => {
      if (redrawTimeoutRef.current) {
        window.clearTimeout(redrawTimeoutRef.current);
        redrawTimeoutRef.current = null;
      }
    };
  }, [redraw]);

  // Handle resize
  useEffect(() => {
    const handleResize = () => {
      redraw();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [redraw]);

  return (
    <canvas
      ref={canvasRef}
      className="pointer-events-none absolute inset-0"
      style={{ zIndex: 0 }} // Set to 0 to be below nodes
    />
  );
};

// Memoize component to prevent re-renders when props haven't changed
// This is critical for canvas rendering performance
export const ClusterCanvas = memo(
  ClusterCanvasComponent,
  (prevProps, nextProps) => {
    // Custom comparison: only re-render if clusters content, graph, or other props changed
    return (
      prevProps.graph === nextProps.graph &&
      prevProps.hasSelection === nextProps.hasSelection &&
      prevProps.currentTheme === nextProps.currentTheme &&
      prevProps.selectedClusterValue === nextProps.selectedClusterValue &&
      prevProps.clusters.length === nextProps.clusters.length &&
      prevProps.clusters.every(
        (cluster, i) =>
          cluster.value === nextProps.clusters[i]?.value &&
          cluster.nodes.length === nextProps.clusters[i]?.nodes.length
      )
    );
  }
);
